import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import DonutProgress from "../../components/DonutProgress/DonutProgress";
import FunnelChart from "../../components/FunnelChart/FunnelChart";
import BottleneckHeatmap from "../../components/BottleneckHeatmap/BottleneckHeatmap";
import TeacherDistribution from "../../components/TeacherDistribution/TeacherDistribution";
import useModalsStore from "../../utils/stores/useModalsStore";
import { getStandardBreakdown } from "../../services/analyticsService";
import {
  DASHBOARD_PERMISSION_CODES,
  hasPermission,
} from "../../utils/permissions";
import { scoreTextClass } from "../../utils/helpers/scoreColors";
import { SkeletonCard } from "../../components/Skeleton/Skeleton";
import RubricGate, { isRubricGated } from "../../components/RubricGate/RubricGate";
import useOrgScope from "../../utils/hooks/useOrgScope";
import {
  buildFunnelSteps,
  computeStandardKpi,
  funnelStepsFromCounts,
  groupTeachersByQuestion,
} from "../../utils/helpers/standardBreakdown";
import { standardScopeLabel } from "../../utils/helpers/standardScope";

export default function StandardDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  // Reached from a school-dashboard row, "back" returns to the dashboard.
  const fromDashboard = useLocation().state?.from === "/";
  const backTo = fromDashboard ? "/" : "/standards";
  const backLabel = t(fromDashboard ? "pageTitles.dashboard" : "standards.back");
  const { user } = useContext(UserContext);
  const canView = hasPermission(user, DASHBOARD_PERMISSION_CODES.SCHOOL_VIEW);
  const { effectiveOrgUuid } = useOrgScope();
  const openTeacherOverview = useModalsStore((s) => s.openTeacherOverview);
  const openTeacherList = useModalsStore((s) => s.openTeacherList);

  const [breakdown, setBreakdown] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [viewMode, setViewMode] = useState("charts");

  useEffect(() => {
    if (!canView) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError("");
    setNotFound(false);
    getStandardBreakdown({
      params: { standard: id, organization: effectiveOrgUuid },
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        setBreakdown(data);
        setIsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === "ERR_CANCELED") return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          setLoadError(t("standards.detailError"));
        }
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [canView, id, effectiveOrgUuid, t]);

  // Across >1 school the payload drops the teacher rows and carries the funnel
  // already reduced, so the same charts are fed from counts and the drill-downs
  // that open a teacher list go away with the rows they would have listed.
  const schoolCount = breakdown?.scope?.school_count || 0;
  const aggregated = schoolCount > 1;

  const steps = useMemo(
    () =>
      aggregated
        ? funnelStepsFromCounts(
            breakdown?.questions || [],
            breakdown?.question_counts,
          )
        : buildFunnelSteps(breakdown?.questions || [], breakdown?.teachers || []),
    [aggregated, breakdown],
  );
  // Without the rows, the funnel's own starting pool is the total every KPI is
  // a share of — it is the same number `teachers.length` gives at one school.
  const totalTeachers = aggregated
    ? steps[0]?.inputCount ?? 0
    : breakdown?.teachers?.length || 0;
  const kpi = useMemo(
    () => computeStandardKpi(steps, totalTeachers),
    [steps, totalTeachers],
  );
  // Built only for the tab that draws it: a Q × T grid of avatar chips is the
  // page's most expensive render and most visits never open it.
  const groups = useMemo(
    () =>
      viewMode === "distribution"
        ? groupTeachersByQuestion(breakdown?.questions || [], breakdown?.teachers || [])
        : [],
    [viewMode, breakdown],
  );

  // Every drill-down from this page is scoped to this standard, so the teacher
  // overview opens focused on it (id = standard uuid).
  const handleTeacherClick = (teacher) =>
    openTeacherOverview({ teacher, focusStandardUuid: id });

  const handleFunnelClick = (type, stepIndex) => {
    if (!breakdown) return;
    if (type === "total") {
      openTeacherList({
        title: t("standards.allTeachersEvaluated", { count: breakdown.teachers.length }),
        teachers: breakdown.teachers.map((row) => row.teacher),
        focusStandardUuid: id,
      });
      return;
    }
    const step = steps[stepIndex];
    if (!step) return;
    // The bar also counts teachers carried past a question their lesson's
    // version never had: its list holds them too, so it cannot say "passed".
    const passTitle = step.notAskedCount > 0 ? "standards.stillInLabel" : "standards.passedLabel";
    openTeacherList({
      title:
        type === "pass"
          ? t(passTitle, { label: step.shortLabel })
          : t("standards.didNotPass", { label: step.shortLabel }),
      teachers: type === "pass" ? step.carriedTeachers : step.droppedTeachers,
      focusStandardUuid: id,
    });
  };

  // Heatmap shows real failure rate (not coverage gaps), so the click
  // drills into actual failures only — not the "did not pass" superset.
  const handleHeatmapClick = (stepIndex) => {
    const step = steps[stepIndex];
    if (!step) return;
    openTeacherList({
      title: t("standards.failedLabel", { label: step.shortLabel }),
      teachers: step.failingTeachers,
      focusStandardUuid: id,
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <Link
            to={backTo}
            className="text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-indigo-600"
          >
            ← {backLabel}
          </Link>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} className="h-28" lines={2} />
            ))}
          </div>
          <SkeletonCard className="mt-6 h-80" lines={6} />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6 text-center text-sm text-rose-600">{loadError}</div>
    );
  }

  // Scope spans >1 active rubric (a head org whose schools diverged): the
  // per-question grid would be incoherent, so prompt a single-school drill-down
  // via the school selector instead. The backend sends standard:null here.
  if (isRubricGated(breakdown?.active_rubrics)) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-6">
            <Link
              to={backTo}
              className="text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-indigo-600"
            >
              ← {backLabel}
            </Link>
          </div>
          <RubricGate activeRubrics={breakdown.active_rubrics} />
        </div>
      </div>
    );
  }

  if (notFound || !breakdown) {
    return (
      <div className="p-6">
        <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-sm text-slate-600">
            {t("standards.notFound", { id })}{" "}
            <Link to="/standards" className="text-indigo-600 hover:underline">
              {t("standards.backToOverview")}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  const std = breakdown.standard || {};
  const scopeLabel = standardScopeLabel(t, std);
  const score = totalTeachers > 0
    ? Math.round(100 - kpi.attritionRate)
    : 0;
  // The distribution panel needs the rows the aggregate does not carry, so at
  // network scope the charts are the only view and the tab pair goes away.
  const showCharts = aggregated || viewMode === "charts";

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between gap-4">
            <Link
              to={backTo}
              className="text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-indigo-600"
            >
              ← {backLabel}
            </Link>
          </div>
          <div className="mt-2 flex items-center justify-between gap-4">
            <div>
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                {t("teacher.standardN", { number: std.number })}
              </span>
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
                {std.title}
              </h2>
              {scopeLabel && (
                <p className="mt-1 text-xs font-semibold text-indigo-600">{scopeLabel}</p>
              )}
            </div>
            {totalTeachers > 0 && (
              <div className="flex flex-col items-center">
                <DonutProgress
                  score={score}
                  size={72}
                  strokeWidth={7}
                  labelSize="text-sm"
                />
                <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-1">
                  {t("standards.passedAll")}
                </span>
              </div>
            )}
          </div>
        </div>

        {totalTeachers === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <p className="text-sm text-slate-500">
              {t("standards.noFinalizedYet")}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {t("standards.chartsWillPopulate")}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  {t("standards.teachersEvaluated")}
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {totalTeachers}
                </p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  {t("standards.passedAllCriteria")}
                </p>
                <p
                  className={`text-2xl font-bold mt-1 ${scoreTextClass(score)}`}
                >
                  {kpi.survivors}
                  <span className="text-sm font-normal text-slate-400 ml-1">
                    / {totalTeachers} ({score}%)
                  </span>
                </p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  {t("standards.weakestIndicator")}
                </p>
                <p className="text-sm font-bold text-slate-900 mt-1 truncate" title={kpi.weakestIndicatorLabel}>
                  {kpi.weakestIndicatorLabel}
                </p>
                <p className="text-xs text-rose-600 font-bold mt-0.5">
                  {t("standards.drop", { pct: Math.round(kpi.weakestIndicatorValue * 100) })}
                </p>
              </div>
            </div>

            {!aggregated && (
            <div className="flex items-center gap-2 bg-white p-1 rounded-xl border border-slate-200 shadow-sm w-fit mb-4">
              <button
                type="button"
                onClick={() => setViewMode("charts")}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  viewMode === "charts"
                    ? "bg-slate-900 text-white shadow-md"
                    : "text-slate-500 hover:bg-slate-50"
                }`}
              >
                {t("standards.performanceCharts")}
              </button>
              <button
                type="button"
                onClick={() => setViewMode("distribution")}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  viewMode === "distribution"
                    ? "bg-slate-900 text-white shadow-md"
                    : "text-slate-500 hover:bg-slate-50"
                }`}
              >
                {t("standards.teacherDistribution")}
              </button>
            </div>
            )}

            {/* What the charts show + that they're clickable (tab-aware). */}
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50/50 p-4">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-xs text-indigo-900/80 leading-relaxed">
                {aggregated
                  ? t("standards.infoNetwork", { count: schoolCount })
                  : viewMode === "charts"
                  ? t("standards.infoCharts")
                  : t("standards.infoDistribution")}
              </p>
            </div>

            {showCharts ? (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-md border border-slate-200 min-h-[400px] flex flex-col">
                  <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-3">
                    <span className="bg-blue-100 text-blue-700 w-8 h-8 flex items-center justify-center rounded-full font-mono text-sm">
                      1
                    </span>
                    {t("standards.volumeFunnel")}
                  </h3>
                  {/* No teacher rows behind an aggregate, so no list to open. */}
                  <FunnelChart
                    data={steps}
                    onSegmentClick={aggregated ? undefined : handleFunnelClick}
                  />
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border border-slate-200 min-h-[400px] flex flex-col">
                  <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-3">
                    <span className="bg-red-100 text-red-700 w-8 h-8 flex items-center justify-center rounded-full font-mono text-sm">
                      2
                    </span>
                    {t("standards.indicatorHeatmap")}
                  </h3>
                  <BottleneckHeatmap
                    data={steps}
                    onCellClick={aggregated ? undefined : handleHeatmapClick}
                  />
                </div>
              </div>
            ) : (
              <div className="bg-white p-6 rounded-xl shadow-md border border-slate-200">
                <h3 className="text-base font-bold text-slate-800 mb-6 flex items-center gap-3">
                  <span className="bg-emerald-100 text-emerald-700 w-8 h-8 flex items-center justify-center rounded-full font-mono text-sm">
                    3
                  </span>
                  {t("standards.distributionByCriteria")}
                </h3>
                <TeacherDistribution
                  groups={groups}
                  onTeacherClick={handleTeacherClick}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
